/**
 * FP-2744 - scan queue for PackingV2.
 *
 * Locally-recognized, non-serialized product scans are queued here instead of
 * one network call per scan. A per-shipment flush loop drains the queue:
 * entries are deduped to one POST per (surface, target, product) with the
 * summed quantity, POSTs run in parallel, then one silent full shipment
 * refresh. The scan input never blocks for queued scans.
 *
 * Flush trigger: a 500ms batch window opens at the first queued scan (later
 * scans join it without resetting it), or completion of the in-flight
 * flush+refresh cycle, whichever is later. Scans slower than the window flush
 * alone - batching only kicks in when scans arrive faster than 500ms apart.
 * Callers also flush early when a product's queued quantity reaches the
 * shipment item's remaining quantity, so over-scans fail on their own POST
 * instead of failing the whole batch.
 *
 * Serialized products, container barcodes, bulk modals, and unresolved scans
 * never enter the queue; callers flush first and run those serially.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
export var SCAN_FLUSH_DEBOUNCE_MS = 500;
// Deduplicate queue entries: one batch item per (surface, target, product),
// quantities summed. Order of first appearance is preserved.
export var groupScanQueueEntries = function (entries) {
    var bySignature = new Map();
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        var target = entry.surface === 'package' ? entry.shipmentPackageSeqId : entry.packIntoContainerId;
        var signature = entry.surface + "|" + target + "|" + entry.productId;
        var existing = bySignature.get(signature);
        if (existing) {
            existing.quantity += entry.quantity;
            existing.scanCount += 1;
        }
        else {
            bySignature.set(signature, {
                surface: entry.surface,
                scanId: entry.scanId,
                productId: entry.productId,
                quantity: entry.quantity,
                scanCount: 1,
                shipmentPackageSeqId: entry.shipmentPackageSeqId,
                packIntoContainerId: entry.packIntoContainerId,
                shipmentBoxTypeId: entry.shipmentBoxTypeId
            });
        }
    }
    return Array.from(bySignature.values());
};
var runtimeByShipmentId = new Map();
var getRuntime = function (shipmentId) {
    var runtime = runtimeByShipmentId.get(shipmentId);
    if (!runtime) {
        runtime = { entries: [], timer: null, flushing: false, activeFlush: null, executor: null, windowClosesAt: null };
        runtimeByShipmentId.set(shipmentId, runtime);
    }
    return runtime;
};
var syncStatus = function (runtime) {
    var _a;
    (_a = runtime.executor) === null || _a === void 0 ? void 0 : _a.onStatus(runtime.entries.reduce(function (sum, entry) { return sum + entry.quantity; }, 0), runtime.flushing);
};
/** (Re)binds the executor. Call from an effect so closures stay fresh. */
export var configureScanQueue = function (shipmentId, executor) {
    getRuntime(shipmentId).executor = executor;
};
var runFlush = function (runtime) { return __awaiter(void 0, void 0, Promise, function () {
    var executor, batch_1, results, failures_1, delay;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                executor = runtime.executor;
                if (!executor)
                    return [2 /*return*/];
                runtime.flushing = true;
                syncStatus(runtime);
                _b.label = 1;
            case 1:
                _b.trys.push([1, , 5, 6]);
                batch_1 = groupScanQueueEntries(runtime.entries.splice(0));
                syncStatus(runtime);
                if (!(batch_1.length > 0)) return [3 /*break*/, 4];
                return [4 /*yield*/, Promise.allSettled(batch_1.map(function (item) {
                        return item.surface === 'package' ? executor.packBatch(item) : executor.containerBatch(item);
                    }))];
            case 2:
                results = _b.sent();
                failures_1 = [];
                results.forEach(function (result, index) {
                    var _a;
                    var item = batch_1[index];
                    if (result.status === 'fulfilled') {
                        (_a = executor.onBatchSuccess) === null || _a === void 0 ? void 0 : _a.call(executor, item);
                    }
                    else {
                        failures_1.push({
                            scanId: item.scanId,
                            productId: item.productId,
                            quantity: item.quantity,
                            scanCount: item.scanCount,
                            message: (result.reason && (result.reason.message || String(result.reason))) ||
                                'Failed to pack item'
                        });
                    }
                });
                // Failed quantities are dropped, never silently retried — the worker
                // re-scans after reading the error.
                if (failures_1.length > 0)
                    executor.onErrors(failures_1);
                return [4 /*yield*/, executor.refresh()];
            case 3:
                _b.sent();
                _b.label = 4;
            case 4: return [3 /*break*/, 6];
            case 5:
                runtime.flushing = false;
                runtime.activeFlush = null;
                syncStatus(runtime);
                if (runtime.entries.length > 0 && !runtime.timer) {
                    delay = Math.max(0, ((_a = runtime.windowClosesAt) !== null && _a !== void 0 ? _a : 0) - Date.now());
                    runtime.timer = setTimeout(function () {
                        runtime.timer = null;
                        startFlush(runtime);
                    }, delay);
                }
                return [7 /*endfinally*/];
            case 6: return [2 /*return*/];
        }
    });
}); };
var startFlush = function (runtime) {
    if (runtime.timer) {
        clearTimeout(runtime.timer);
        runtime.timer = null;
    }
    if (runtime.flushing)
        return;
    runtime.activeFlush = runFlush(runtime);
};
/**
 * Enqueue a scan. The first entry of an accumulation opens the 500ms batch
 * window; later entries join it without resetting it. If a flush is in
 * flight, the window still opens now — the flush's completion schedules the
 * next cycle for whatever is left of it ("whichever is later").
 * Returns the new pending quantity.
 */
export var enqueueScan = function (shipmentId, entry) {
    var runtime = getRuntime(shipmentId);
    runtime.entries.push(entry);
    if (runtime.entries.length === 1) {
        runtime.windowClosesAt = Date.now() + SCAN_FLUSH_DEBOUNCE_MS;
    }
    syncStatus(runtime);
    if (!runtime.flushing && !runtime.timer) {
        runtime.timer = setTimeout(function () {
            runtime.timer = null;
            startFlush(runtime);
        }, SCAN_FLUSH_DEBOUNCE_MS);
    }
    return runtime.entries.reduce(function (sum, queued) { return sum + queued.quantity; }, 0);
};
/**
 * Drain the queue now and resolve when the flush+refresh cycle completes.
 * Call before any serial operation that depends on server state (container
 * switch/close, package close, blocking legacy scans, leaving the screen).
 */
export var flushScanQueueNow = function (shipmentId) { return __awaiter(void 0, void 0, Promise, function () {
    var runtime;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                runtime = runtimeByShipmentId.get(shipmentId);
                if (!runtime)
                    return [2 /*return*/];
                _a.label = 1;
            case 1:
                if (!runtime.flushing && runtime.entries.length > 0)
                    startFlush(runtime);
                if (!runtime.activeFlush)
                    return [2 /*return*/];
                return [4 /*yield*/, runtime.activeFlush];
            case 2:
                _a.sent();
                _a.label = 3;
            case 3: return [3 /*break*/, 1];
            case 4: return [2 /*return*/];
        }
    });
}); };
export var getScanQueuePendingCount = function (shipmentId) {
    var runtime = runtimeByShipmentId.get(shipmentId);
    if (!runtime)
        return 0;
    return runtime.entries.reduce(function (sum, entry) { return sum + entry.quantity; }, 0);
};
/**
 * Queued (not yet flushed) quantity for one product, across both surfaces.
 * Used for the item-cap early flush: when this reaches the shipment item's
 * remaining quantity, waiting out the debounce would only let over-scans pile
 * into the same batch and fail the whole POST.
 */
export var getQueuedQuantityForProduct = function (shipmentId, productId) {
    var runtime = runtimeByShipmentId.get(shipmentId);
    if (!runtime)
        return 0;
    return runtime.entries.reduce(function (sum, entry) { return (entry.productId === productId ? sum + entry.quantity : sum); }, 0);
};
/** Drop all queued scans (shipment unload/reset — queued work is abandoned). */
export var clearScanQueue = function (shipmentId) {
    var runtime = runtimeByShipmentId.get(shipmentId);
    if (!runtime)
        return;
    if (runtime.timer)
        clearTimeout(runtime.timer);
    runtime.entries = [];
    runtime.timer = null;
    runtime.windowClosesAt = null;
    syncStatus(runtime);
    if (!runtime.flushing)
        runtimeByShipmentId.delete(shipmentId);
};
